import { createContext, useContext, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import { exitGuestMode } from '../lib/guestMode';

const GuestPromptContext = createContext({
  requireAuth: () => false,
  promptSignIn: () => {},
});

export const GuestPromptProvider = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { theme, isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState('');

  // Returns true when the caller should abort because the user is unauthenticated.
  // Opens the prompt modal as a side-effect.
  const requireAuth = useCallback((featureLabel) => {
    if (user) return false;
    setFeature(featureLabel || 'do this');
    setOpen(true);
    return true;
  }, [user]);

  const promptSignIn = useCallback((featureLabel) => {
    setFeature(featureLabel || 'do this');
    setOpen(true);
  }, []);

  const close = () => setOpen(false);

  const goLogin = () => {
    exitGuestMode();
    setOpen(false);
    navigate('/login');
  };

  const goSignup = () => {
    exitGuestMode();
    setOpen(false);
    navigate('/signup');
  };

  return (
    <GuestPromptContext.Provider value={{ requireAuth, promptSignIn }}>
      {children}
      {open && (
        <div
          onClick={close}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 360,
              background: theme.bgSheet,
              border: `1px solid ${theme.border}`,
              borderRadius: 16,
              padding: '24px 20px',
              textAlign: 'center',
            }}
          >
            <h2 style={{ color: theme.text, fontSize: 18, fontWeight: 700, margin: '0 0 8px 0' }}>
              Create an account to {feature}
            </h2>
            <p style={{ color: theme.textSecondary, fontSize: 14, margin: '0 0 20px 0', lineHeight: 1.4 }}>
              You're browsing as a guest. Sign in or create an account to interact with traders.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={goSignup}
                style={{
                  width: '100%', padding: '12px 16px',
                  background: theme.accent, color: '#fff',
                  border: 'none', borderRadius: 10,
                  fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Create Account
              </button>
              <button
                onClick={goLogin}
                style={{
                  width: '100%', padding: '12px 16px',
                  background: 'transparent',
                  color: theme.accent,
                  border: `1px solid ${theme.accent}`,
                  borderRadius: 10,
                  fontSize: 15, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Sign In
              </button>
              <button
                onClick={close}
                style={{
                  width: '100%', padding: '10px 16px',
                  background: 'transparent',
                  color: isDark ? '#8a8a8a' : theme.textMuted,
                  border: 'none',
                  fontSize: 14, cursor: 'pointer',
                  marginTop: 4,
                }}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </GuestPromptContext.Provider>
  );
};

export const useGuestPrompt = () => useContext(GuestPromptContext);
